import { useCallback, useEffect, useState } from "react";
import { HttpError, request } from "../../../api/http";
import { useAuth } from "../../../context/AuthContext";

const TYPE_LABELS: Record<string, string> = {
  ID: "DNI / NIE (ID)",
  RP: "Permiso de residencia (RP)",
  PA: "Pasaporte (PA)",
  DL: "Carnet de conducir (DL)",
};

type ApiResponse = {
  documentType: string | null;
  allowedTypes: string[];
};

type Props = {
  leadUlid: string;
  onAfterChange: () => void;
};

export default function IdentificationTypeSelector({
  leadUlid,
  onAfterChange,
}: Props) {
  const { token } = useAuth();
  const [data, setData] = useState<ApiResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await request<ApiResponse>(
        `/api/admin/operations/leads/${leadUlid}/identification`,
        { token },
      );
      setData(response);
    } catch (err) {
      setError(err instanceof HttpError ? `HTTP ${err.status}` : "No se pudo cargar");
    } finally {
      setLoading(false);
    }
  }, [leadUlid, token]);

  useEffect(() => {
    load();
  }, [load]);

  const save = async (next: string) => {
    if (saving) return;
    setSaving(true);
    setError(null);
    try {
      const response = await request<{ documentType: string }>(
        `/api/admin/operations/leads/${leadUlid}/identification`,
        {
          method: "PATCH",
          token,
          body: { documentType: next },
        },
      );
      setData((prev) =>
        prev === null
          ? null
          : { ...prev, documentType: response.documentType },
      );
      onAfterChange();
    } catch (err) {
      setError(err instanceof HttpError ? `HTTP ${err.status}` : "No se pudo guardar");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div style={{ color: "#73879C", fontSize: ".85rem", padding: "0.4rem 0" }}>
        Cargando tipo de documento…
      </div>
    );
  }

  const allowed = data?.allowedTypes ?? ["ID", "RP", "PA", "DL"];
  const current = data?.documentType ?? "";

  return (
    <div
      style={{
        background: "white",
        border: "1px solid #e9ecef",
        borderLeft: "3px solid #0069d9",
        borderRadius: "0.55rem",
        padding: "0.6rem 0.9rem",
        display: "flex",
        alignItems: "center",
        gap: "0.75rem",
        flexWrap: "wrap",
      }}
    >
      <span
        style={{
          fontSize: ".7rem",
          textTransform: "uppercase",
          fontWeight: 700,
          color: "#0069d9",
          letterSpacing: ".5px",
        }}
      >
        Tipo de documento
      </span>
      <select
        value={current}
        disabled={saving}
        onChange={(e) => save(e.target.value)}
        style={{
          padding: "0.35rem 0.55rem",
          borderRadius: "0.4rem",
          border: "1px solid #dee2e6",
          background: "#f8f9fb",
          fontSize: ".85rem",
          minWidth: 220,
        }}
      >
        {current === "" && <option value="">— Sin definir —</option>}
        {allowed.map((t) => (
          <option key={t} value={t}>
            {TYPE_LABELS[t] ?? t}
          </option>
        ))}
      </select>
      {saving && (
        <span style={{ color: "#73879C", fontSize: ".75rem" }}>Guardando…</span>
      )}
      {error && (
        <span style={{ color: "#b32424", fontSize: ".75rem" }}>{error}</span>
      )}
    </div>
  );
}
